import React, { useState, useRef, useEffect } from "react";
import { apiCall } from "@/api/apiCall";
import { toast } from "sonner";
import { useApiQueue } from "@/hooks/useApiQueue";
import TaifContract from "@/assets/img/moc_logo.png";
import CarPark from "@/assets/img/TaifContract-carPark.png";
import OnStreet from "@/assets/img/TaifContract-onStreet.png";
import Enforcement from "@/assets/img/TaifContract-Enforcement.png";
import { AssetsPanel } from "../../components/shared/AssetsPanel";
import { API_CALL_TIME } from "@/constants/constant";

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════
const groupsToFlat = (groups = []) => {
  const result = [];
  groups.forEach((g) => {
    result.push({
      name: g.group_name || "",
      isCategory: true,
      subCount: "",
      totalCount: String(g.group_total_qty ?? "-"),
    });
    (g.items || []).forEach((item) => {
      result.push({
        name: item.item_name || "",
        isCategory: false,
        subCount: String(item.qty ?? "-"),
        totalCount: "",
      });
    });
  });
  return result;
};

const flatToGroups = (flat) => {
  const groups = [];
  let cur = null;
  flat.forEach((a) => {
    if (a.isCategory) {
      if (cur) groups.push(cur);
      cur = { group_name: a.name, group_total_qty: a.totalCount || "", items: [] };
    } else if (cur) {
      cur.items.push({ item_name: a.name, qty: a.subCount || "" });
    }
  });
  if (cur) groups.push(cur);
  return groups;
};

// ═══════════════════════════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════════════════════════
const TAIF_PROFILE_CONFIG = {
  moc_on_street_profile: {
    profileKey: "moc_on_street_profile",
    violationKey: "taif_on_street_violations",
    towingKey: "taif_on_street_towing",
    clampKey: "taif_on_street_clamp",
    assetsKey: "taif_on_street_assets",

    profileFallback: {
      table_key: "moc_on_street_profile",
      title: "MOC On Street - Profile",
      data: {
        total_bays: { label: "MOC On Street Parking  Bays", value: "2,533" },
        contract_term: { value: "5 years" },
        contract_period: { start_date: "07.12.2025", end_date: "06.12.2030" },
        parking_violation: { value: "Parking and Violation charges are as per below table" },
        membership_tariff: {
          label: "Membership and Enforcement tariff",
          value: "excluding 15% VAT",
        },
      },
    },

    violationFallback: {
      table_key: "taif_on_street_violations",
      title: "Charge Notice Fees: On-street",
      data: {
        rows: [
          {
            group: "1st Violation Group",
            violation_code: "1.01",
            violation_type: "The use of parking space without paying the parking fee",
            amount: "200",
            escalation: "2nd violation - Clamping\n3rd violation - Removal to the pound",
          },
          {
            group: "",
            violation_code: "1.02",
            violation_type:
              "The expiry of the paid parking period without vacating the parking space",
            amount: "100",
            escalation: "2nd violation - Clamping\n3rd violation - Removal to the pound",
          },
          {
            group: "",
            violation_code: "1.03",
            violation_type:
              "Exceeding the maximum time allowed for the parking space (Max 24 hours)",
            amount: "100",
            escalation: "2nd violation - Clamping\n3rd violation - Removal to the pound",
          },
          {
            group: "",
            violation_code: "1.04",
            violation_type: "Parking contrarily within the paid parking space",
            amount: "100",
            escalation: "2nd violation - Clamping\n3rd violation - Removal to the pound",
          },
          {
            group: "2nd Violation Group",
            violation_code: "1.05",
            violation_type: "Parking in a reserved parking space",
            amount: "300",
            escalation: "Clamping/Towing/Pound",
          },
          {
            group: "",
            violation_code: "1.06",
            violation_type: "Parking in a restricted space",
            amount: "300",
            escalation: "Clamping/Towing/Pound",
          },
          {
            group: "3rd Violation Group",
            violation_code: "1.07",
            violation_type:
              "Placing barriers, obstructions, forgery, or closing a parking space without a permit",
            amount: "400",
            escalation: "Cost of removing",
          },
          {
            group: "4th Violation Group",
            violation_code: "1.08",
            violation_type: "Parking in the emergency designated areas",
            amount: "900",
            escalation: "Towing/Pound",
          },
          {
            group: "",
            violation_code: "1.09",
            violation_type: "Parking at the entrances and exits of buildings",
            amount: "500",
            escalation: "Towing/Pound",
          },
        ],
      },
    },

    towingFallback: {
      table_key: "taif_on_street_towing",
      title: "Towing and Vehicle Storage Charges",
      data: {
        subtitle:
          "Vehicles that cannot be towed for any reason will be clamped and clamp fees will be applied",
        towing_light: "250",
        towing_large: "1,250",
        storage_light: "20",
        storage_large: "50",
      },
    },

    clampFallback: {
      table_key: "taif_on_street_clamp",
      title: "Clamp Removal Fee",
      data: {
        clamp_light: "-",
        clamp_large: "-",
        broken_clamp: "1,100",
      },
    },

    assetsFallback: {
      table_key: "taif_on_street_assets",
      title: "MOC  On-Street Profile",
      data: {
        groups: [
          {
            group_name: "Ticket Dispenser",
            group_total_qty: "0",
            items: [
              { item_name: "DIP G100", qty: "0" },
              { item_name: "G100 64 Bit", qty: "0" },
              { item_name: "G100 32 Bit", qty: "0" },
            ],
          },
          {
            group_name: "Ticket Verifier",
            group_total_qty: "0",
            items: [
              { item_name: "EV DIP G100", qty: "0" },
              { item_name: "EV G100 64 Bit", qty: "0" },
              { item_name: "EV G100 32 Bit", qty: "0" },
            ],
          },
          {
            group_name: "APM's",
            group_total_qty: "0",
            items: [
              { item_name: "APM ETS-70", qty: "0" },
              { item_name: "APM ETS-100", qty: "0" },
            ],
          },
          {
            group_name: "PIN Pad",
            group_total_qty: "0",
            items: [{ item_name: "IUP250", qty: "0" }],
          },
          {
            group_name: "Card Reader",
            group_total_qty: "0",
            items: [{ item_name: "IUR250", qty: "0" }],
          },
          {
            group_name: "B-Contactless",
            group_total_qty: "0",
            items: [{ item_name: "IUC150", qty: "0" }],
          },
          {
            group_name: "Banking All-in-One",
            group_total_qty: "68",
            items: [{ item_name: "S4000", qty: "68" }],
          },
          { group_name: "RFID Reader", group_total_qty: "0", items: [] },
          { group_name: "IP Barrier", group_total_qty: "0", items: [] },
          {
            group_name: "ANPR Camera",
            group_total_qty: "0",
            items: [
              { item_name: "ARH", qty: "0" },
              { item_name: "Vidar", qty: "0" },
              { item_name: "Luminera", qty: "0" },
            ],
          },
          {
            group_name: "Sliding Gate",
            group_total_qty: "0",
            items: [
              { item_name: "RIGHT", qty: "0" },
              { item_name: "LEFT", qty: "0" },
            ],
          },
          {
            group_name: "PND's",
            group_total_qty: "68",
            items: [
              { item_name: "TPAL- BNA Large", qty: "0" },
              { item_name: "T-PAL TX", qty: "0" },
              { item_name: "T-PAL V2", qty: "0" },
              { item_name: "STREET SMART BNA GEN3", qty: "68" },
              { item_name: "STREET SMART TX GEN3", qty: "0" },
            ],
          },
          {
            group_name: "Pound Cahier Device",
            group_total_qty: "0",
            items: [{ item_name: "CN-50", qty: "0" }],
          },
          {
            group_name: "Driver Device",
            group_total_qty: "0",
            items: [{ item_name: "CN-51", qty: "0" }],
          },
          {
            group_name: "Enf Device",
            group_total_qty: "5",
            items: [
              { item_name: "Oukitel WP2", qty: "0" },
              { item_name: "Doogee V10", qty: "5" },
            ],
          },
          {
            group_name: "Validation",
            group_total_qty: "0",
            items: [{ item_name: "Lenova Tablet", qty: "0" }],
          },
          {
            group_name: "Printer",
            group_total_qty: "20",
            items: [
              { item_name: "PR-3", qty: "0" },
              { item_name: "SEWOO", qty: "0" },
              { item_name: "BIXOLON", qty: "20" },
            ],
          },
          { group_name: "Crony D2 Pro", group_total_qty: "8", items: [] },
          { group_name: "Tow Truck", group_total_qty: "0", items: [] },
          { group_name: "Clamp Vehicle", group_total_qty: "0", items: [] },
          { group_name: "Support Vehicle", group_total_qty: "0", items: [] },
        ],
      },
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const MocProfile = ({ page, year = "", month = "", monthNumber = "", contract = "" }) => {
  const cfg = TAIF_PROFILE_CONFIG[page?.type];
  const _cfg = cfg || TAIF_PROFILE_CONFIG["moc_on_street_profile"];

  const hasFetchedRef = useRef(false);
  const saveRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [profileTable, setProfileTable] = useState(_cfg.profileFallback);
  const [violationTable, setViolationTable] = useState(_cfg.violationFallback);
  const [towingTable, setTowingTable] = useState(_cfg.towingFallback);
  const [clampTable, setClampTable] = useState(_cfg.clampFallback);
  const [assetsTable, setAssetsTable] = useState(_cfg.assetsFallback);
  const [parkingBaysData, setParkingBaysData] = useState(null);

  const { enqueue } = useApiQueue();

  const effectiveTotalBays =
    parkingBaysData?.total_bays ?? profileTable.data.total_bays?.value ?? "0";

  const profileRef = useRef(null);
  const violationRef = useRef(null);
  const towingRef = useRef(null);
  const clampRef = useRef(null);
  const assetsRef = useRef(null);

  useEffect(() => {
    profileRef.current = profileTable;
  }, [profileTable]);
  useEffect(() => {
    violationRef.current = violationTable;
  }, [violationTable]);
  useEffect(() => {
    towingRef.current = towingTable;
  }, [towingTable]);
  useEffect(() => {
    clampRef.current = clampTable;
  }, [clampTable]);
  useEffect(() => {
    assetsRef.current = assetsTable;
  }, [assetsTable]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    (async () => {
      try {
        const [tablesRes, baysRes] = await Promise.all([
          enqueue(() =>
            apiCall("GET", "/boardpack/get-tables-data", { year, contract, month: monthNumber })
          ),
          enqueue(() =>
            apiCall("GET", "/boardpack/parking/bays", {
              contract: "0020025",
              year,
              month,
              device_onstreet: "MOC",
              type: "on_street",
            })
          ),
        ]);
        if (tablesRes?.success) {
          const data = tablesRes?.data || [];
          const find = (key, fb) => data.find((t) => t.table_key === key) || fb;
          setProfileTable(find(cfg.profileKey, cfg.profileFallback));
          setViolationTable(find(cfg.violationKey, cfg.violationFallback));
          setTowingTable(find(cfg.towingKey, cfg.towingFallback));
          setClampTable(find(cfg.clampKey, cfg.clampFallback));
          setAssetsTable(find(cfg.assetsKey, cfg.assetsFallback));
        }
        if (baysRes?.success && baysRes?.data) setParkingBaysData(baysRes.data);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    })();
  }, [year, month, monthNumber, contract]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoSave = () => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const tables = [
        {
          table_key: profileRef.current?.table_key,
          title: profileRef.current?.title,
          data: profileRef.current?.data,
        },
        {
          table_key: violationRef.current?.table_key,
          title: violationRef.current?.title,
          data: violationRef.current?.data,
        },
        {
          table_key: towingRef.current?.table_key,
          title: towingRef.current?.title,
          data: towingRef.current?.data,
        },
        {
          table_key: clampRef.current?.table_key,
          title: clampRef.current?.title,
          data: clampRef.current?.data,
        },
        {
          table_key: assetsRef.current?.table_key,
          title: assetsRef.current?.title,
          data: assetsRef.current?.data,
        },
      ];
      const res = await apiCall("POST", "/boardpack/add-tables-data", {
        contract,
        year,
        month: monthNumber,
        created_by: 1,
        tables,
      });
      if (!res?.success) throw new Error(res?.message || "Save failed");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────
  const patchProfile = (field, subField, value) => {
    setProfileTable((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: subField ? { ...prev.data[field], [subField]: value } : value,
      },
    }));
    autoSave();
  };

  const handleViolationCell = (rIdx, key, value) => {
    setViolationTable((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((r, i) => (i === rIdx ? { ...r, [key]: value } : r)),
      },
    }));
    autoSave();
  };

  const handleTowingField = (key, value) => {
    setTowingTable((prev) => ({ ...prev, data: { ...prev.data, [key]: value } }));
    autoSave();
  };

  const handleClampField = (key, value) => {
    setClampTable((prev) => ({ ...prev, data: { ...prev.data, [key]: value } }));
    autoSave();
  };

  const handleAssetSubCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current?.data?.groups || []).map((a, i) =>
      i === flatIdx ? { ...a, subCount: value } : a
    );
    setAssetsTable((prev) => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  const handleAssetTotalCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current?.data?.groups || []).map((a, i) =>
      i === flatIdx ? { ...a, totalCount: value } : a
    );
    setAssetsTable((prev) => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  // ── Derived ───────────────────────────────────────────────────────────
  if (!cfg) return null;

  const pd = profileTable.data;
  const td = towingTable.data;
  const cd = clampTable.data;
  const flatAssets = groupsToFlat(assetsTable.data.groups);

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen p-1 bg-white">
      {/* ── Header ── */}
      <div className="flex justify-between items-center mb-3">
        <div className="ml-2">
          <img
            src={TaifContract}
            alt="Bab Al Ree Logo"
            style={{ height: "50px", objectFit: "contain" }}
          />
        </div>
        <h1 className="text-xl font-bold flex-1 text-center">{profileTable.title}</h1>
        {/* {isSaving && <span className="text-xs text-gray-400 mr-2">Saving...</span>} */}
      </div>

      <div className="flex flex-row gap-0 !border-2 !border-black p-3">
        {/* ── LEFT ── */}
        <div className="flex-1 pr-2" style={{ zoom: "90%" }}>
          {/* Activities icons */}
          <div className="mb-4">
            <p className="text-[16px] font-bold mb-3">Activities:</p>
            <div className="flex gap-0 justify-start ml-16">
              {["On Street", "Car Park", "Enforcement"].map((label) => (
                <div key={label} className="flex flex-col items-center gap-2">
                  {label === "On Street" && (
                    <img
                      src={OnStreet}
                      alt="On Street"
                      style={{ height: "140px", objectFit: "contain" }}
                    />
                  )}
                  {label === "Car Park" && (
                    <img
                      src={CarPark}
                      alt="Car Park"
                      style={{ height: "150px", objectFit: "contain" }}
                    />
                  )}
                  {label === "Enforcement" && (
                    <img
                      src={Enforcement}
                      alt="Enforcement"
                      style={{ height: "140px", objectFit: "contain" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total Bays — read-only from API */}
          <div className="flex items-center gap-2 mb-1 ml-2">
            <div className="w-10 h-8 bg-gray-100 rounded flex items-center justify-center">
              <img src={CarPark} alt="Car Park" style={{ height: "50px", objectFit: "contain" }} />
            </div>
            <span className="text-[12px]">
              {pd.total_bays?.label}{" "}
              <strong>
                <input
                  type="text"
                  value={effectiveTotalBays}
                  readOnly
                  disabled
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-16 cursor-default"
                />
              </strong>
            </span>
          </div>

          {/* Contract Term */}
          <div className="flex items-center gap-2 mb-1 ml-8">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px] flex items-center gap-1 whitespace-nowrap">
              Contract term{" "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_term?.value || ""}
                  onChange={(e) => patchProfile("contract_term", "value", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-12"
                />
              </strong>
              ; start date{" "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_period?.start_date || ""}
                  onChange={(e) => patchProfile("contract_period", "start_date", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-16"
                />
              </strong>
              {" to "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_period?.end_date || ""}
                  onChange={(e) => patchProfile("contract_period", "end_date", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20"
                />
              </strong>
            </span>
          </div>

          {/* Parking Violation Note */}
          <div className="flex items-center gap-2 mb-1 ml-8">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              <input
                type="text"
                value={pd.parking_violation?.value || ""}
                onChange={(e) => patchProfile("parking_violation", "value", e.target.value)}
                className="outline-none bg-transparent !border-0 text-[12px] w-80 font-semibold"
              />
            </span>
          </div>

          {/* Assets Deployed */}
          <div className="flex items-center gap-2 mb-1 ml-8">
            <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-[12px]">Assets Deployed</span>
          </div>

          {/* Membership Tariff Note */}
          {/* <div className="flex items-center gap-2 mb-5 ml-8">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              {pd.membership_tariff?.label} */}
              {/* <strong> excluding 3% VAT</strong> */}
              {/* <strong> */}
                {" "}
                {/* <span className="text-[12px]">
                  <input
                    type="text"
                    value={pd.membership_tariff?.value || ""}
                    onChange={(e) => patchProfile("membership_tariff", "value", e.target.value)}
                    className="outline-none bg-transparent !border-0 text-[12px] w-80 font-semibold"
                  />
                </span>
              </strong>
            </span>
          </div> */}

          <div className="flex flex-row gap-2">
            {/* ── Charge Notice Fees: On-street ── */}
            <div className="mb-5 ">
              <h2 className="font-bold text-[9px] mb-2">{violationTable.title}</h2>
              <table className="!border-collapse w-full text-[8px] min-w-[600px]">
                <thead>
                  <tr className="bg-gray-200">
                    {["Violation Codes", "Violation Types", "Amount", "Escalation"].map((h) => (
                      <th
                        key={h}
                        className="!border !border-gray-400 px-1.5 py-1 font-bold text-center"
                        style={{
                          width:
                            h === "Violation Codes"
                              ? "55px"
                              : h === "Amount"
                                ? "55px"
                                : h === "Escalation"
                                  ? "55px"
                                  : "180px",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {violationTable.data.rows.map((row, rIdx) => (
                    <React.Fragment key={rIdx}>
                      {row.group && (
                        <tr className="">
                          <td
                            colSpan={4}
                            className="!border !border-gray-400 px-1 py-0.5 text-center "
                          >
                            <input
                              type="text"
                              value={row.group}
                              onChange={(e) => handleViolationCell(rIdx, "group", e.target.value)}
                              className="!border-0 bg-transparent outline-none text-[9px] font-semibold text-center w-full"
                            />
                          </td>
                        </tr>
                      )}
                      <tr className="bg-white">
                        <td className="!border !border-gray-400 px-1 py-0.5 text-center">
                          <input
                            type="text"
                            value={row.violation_code || ""}
                            onChange={(e) =>
                              handleViolationCell(rIdx, "violation_code", e.target.value)
                            }
                            className="!border-0 bg-transparent outline-none  text-center w-full"
                          />
                        </td>
                        {/* <td className="!border !border-gray-400 px-1 py-0.5">
                        <input
                          type="text"
                          value={row.violation_type || ""}
                          onChange={(e) =>
                            handleViolationCell(rIdx, "violation_type", e.target.value)
                          }
                          className="!border-0 bg-transparent outline-none  w-full"
                        />
                      </td> */}
                        <td className="!border !border-gray-400 px-1 py-0.5">
                          <textarea
                            value={row.violation_type || ""}
                            onChange={(e) =>
                              handleViolationCell(rIdx, "violation_type", e.target.value)
                            }
                            rows={2}
                            className="!border-0 bg-transparent outline-none resize-none w-full text-[8px]"
                          />
                        </td>
                        <td className="!border !border-gray-400 px-1 py-0.5 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            <span className="text-[9px]">⃁</span>
                            <input
                              type="text"
                              value={row.amount || ""}
                              onChange={(e) => handleViolationCell(rIdx, "amount", e.target.value)}
                              className="!border-0 bg-transparent outline-none text-center w-12"
                            />
                          </div>
                        </td>
                        <td className="!border !border-gray-400 px-1 py-0.5">
                          <textarea
                            value={row.escalation || ""}
                            onChange={(e) =>
                              handleViolationCell(rIdx, "escalation", e.target.value)
                            }
                            rows={2}
                            className="!border-0 bg-transparent outline-none resize-none w-[100px]"
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col">
              {/* ── Towing and Vehicle Storage Charges ── */}
              <div className="mb-5">
                <h2 className="font-bold text-[9px] mb-2">{towingTable.title}</h2>
                {/* <input
                  type="text"
                  value={td.subtitle || ""}
                  onChange={(e) => handleTowingField("subtitle", e.target.value)}
                  className="!border-0 bg-transparent outline-none text-[8px] mb-2 w-full"
                /> */}
                <table className="!border-collapse text-[8px] min-w-[500px]">
                  <thead>
                    <tr className="bg-gray-200">
                      {[
                        { label: "Towing/Removals", w: "55px" },
                        { label: "Light Vehicles/Motorbikes", w: "55px" },
                        { label: "Large Vehicles/Buses Trucks and Vans", w: "55px" },
                        {
                          label: "Daily Storage - Light Vehicles/Motorbikes\n(Incurred from day 2)",
                          w: "55px",
                        },
                        {
                          label:
                            "Daily Storage - Large Vehicles/Buses,\nTrucks and Vans\n(Incurred from day 2)",
                          w: "55px",
                        },
                      ].map(({ label, w }) => (
                        <th
                          key={label}
                          className="!border !border-gray-400 px-1 py-0.5 text-center font-bold whitespace-pre-line text-[9px]"
                          style={{ width: w }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="!border !border-gray-400 px-2 py-2 text-center text-[8px]"></td>
                      {["towing_light", "towing_large", "storage_light", "storage_large"].map(
                        (key) => (
                          <td
                            key={key}
                            className="!border !border-gray-400 px-1 py-0.5 text-center"
                          >
                            <div className="flex items-center justify-center gap-0.5">
                              <span className="text-[9px]">⃁</span>
                              <input
                                type="text"
                                value={td[key] || ""}
                                onChange={(e) => handleTowingField(key, e.target.value)}
                                className="!border-0 bg-transparent outline-none text-[8px] text-center w-18"
                              />
                            </div>
                          </td>
                        )
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ── Clamp Removal Fee ── */}
              <div>
                <h2 className="font-semibold text-[9px] mb-2">{clampTable.title}</h2>
                <table className="!border-collapse text-[8px] min-w-[500px]">
                  <thead>
                    <tr className="bg-gray-200">
                      {[
                        "Light Vehicles/Motorbikes",
                        "Large Vehicles/Buses Trucks and Vans",
                        "Broken Clamp Fee",
                      ].map((h) => (
                        <th
                          key={h}
                          className="!border !border-gray-400 px-1 py-0.5 text-center font-bold text-[9px]"
                          style={{ width: "55px" }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      {[
                        { key: "clamp_light", symbol: false },
                        { key: "clamp_large", symbol: false },
                        { key: "broken_clamp", symbol: true },
                      ].map(({ key, symbol }) => (
                        <td key={key} className="!border !border-gray-400 px-1 py-0.5 text-center">
                          {symbol ? (
                            <div className="flex items-center justify-center gap-0.5">
                              <span className="text-[9px]">⃁</span>
                              <input
                                type="text"
                                value={cd[key] || ""}
                                onChange={(e) => handleClampField(key, e.target.value)}
                                className="!border-0 bg-transparent outline-none text-[8px] text-center w-24"
                              />
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={cd[key] || ""}
                              onChange={(e) => handleClampField(key, e.target.value)}
                              className="!border-0 bg-transparent outline-none text-[8px] text-center w-24"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Assets Panel ── */}
        <AssetsPanel
          assetsTable={assetsTable}
          flatAssets={flatAssets}
          onSubCount={handleAssetSubCount}
          onTotalCount={handleAssetTotalCount}
        />
      </div>
    </div>
  );
};

export default MocProfile;
